// expenseSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
const apiUrl = process.env.REACT_APP_API_URL;
// Async thunk to fetch expenses
export const fetchExpenses = createAsyncThunk(
  'expenses/fetchExpenses',
  async ({ clientId, financialYear }) => {
    const response = await fetch(`${apiUrl}/api/expenses?clientId=${clientId}&financialYear=${financialYear}`);
    if (!response.ok) {
      throw new Error('Failed to fetch expenses');
    }
    const data = await response.json();
    return data;
  }
);

// Async thunk to fetch categories
export const fetchCatgories = createAsyncThunk(
  'expenses/fetchCatgories',
  async (_,{getState}) => {
    const response = await fetch(`${apiUrl}/api/expenses/categories`);
    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }
    const data = await response.json();
    return data;
  }
);

// Async thunk to add a new expense
export const addExpense = createAsyncThunk(
  'expenses/addExpense',
  async (newExpense) => {
    const response = await fetch(`${apiUrl}/api/expenses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newExpense),
    });
    if (!response.ok) {
      throw new Error('Failed to add expense');
    }
    const data = await response.json();
    return data;
  }
);

// Async thunk to add multiple expenses in bulk
export const addExpensesBulk = createAsyncThunk(
  'expenses/addExpensesBulk',
  async (newExpenses) => {
    const response = await fetch(`${apiUrl}/api/expenses/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newExpenses),
    });
    if (!response.ok) {
      throw new Error('Failed to add expenses');
    }
    const data = await response.json();
    return data;
  }
);

// Async thunk to update an existing expense
export const updateExpense = createAsyncThunk(
  'expenses/updateExpense',
  async ({ id, ...updatedExpense }) => {
    const response = await fetch(`${apiUrl}/api/expenses/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedExpense),
    });
    if (!response.ok) {
      throw new Error('Failed to update expense');
    }
    const data = await response.json();
    return data;
  }
);

// Async thunk to delete an expense
export const deleteExpense = createAsyncThunk(
  'expenses/deleteExpense',
  async (id) => {
    const response = await fetch(`${apiUrl}/api/expenses/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete expense');
    }
    return id; // Return the id of the deleted expense
  }
);

const expenseSlice = createSlice({
  name: 'expenses',
  initialState: {
    expenses: [],
    categories:[],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatgories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCatgories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCatgories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchExpenses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = action.payload;
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses.push(action.payload);
      })
      .addCase(addExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addExpensesBulk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addExpensesBulk.fulfilled, (state, action) => {
        state.loading = false;
        // append returned array of created expenses
        state.expenses = state.expenses.concat(action.payload);
      })
      .addCase(addExpensesBulk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = state.expenses.map((expense) =>
          expense.id === action.payload.id ? action.payload : expense
        );
      })
      .addCase(updateExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = state.expenses.filter((expense) => expense.id !== action.payload);
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default expenseSlice.reducer;
